import BankCodeType from "./bankCodeType";
import type CustomerRefundCandidateCounterpartyBankRequest from "./customerRefundCandidateCounterpartyBankRequest";
import type CustomerRefundCandidateCounterpartyBank from "./customerRefundCandidateCounterpartyBankResponse";
import RefundCounterpartyBank from "./refundCounterpartyBankRequest";
import TransferCounterpartyBankResponse from "./transferCounterpartyBankResponse";
import type TransferRefundCounterpartyBankRequest from "./transferRefundCounterpartyBankRequest";
import type { EntityValidationRules } from "../services/validationService";

/**
 * Validační pravidla pro platby
 *
 * Detaily viz. https://abok.cnb.cz/aboks/podminky_abo-k_priloha_2.pdf
 * Domestic - 3.1.2.2. PRT – Příkaz ke korunové úhradě
 * SEPA - 3.1.2.3. PRE – Příkaz k euroúhradě
 * Foreign - 3.1.2.4. PRZ – Příkaz k úhradě do zahraničí
 * https://www.cnb.cz/export/sites/cnb/cs/platebni-styk/.galleries/sluzby_pro_klienty/download/Prikaz_k_uhrade_do_zahranici_vysvetlivky.pdf
 */

export type CounterpartyBank =
  | CustomerRefundCandidateCounterpartyBank
  | CustomerRefundCandidateCounterpartyBankRequest
  | RefundCounterpartyBank
  | TransferRefundCounterpartyBankRequest
  | TransferCounterpartyBankResponse;

export const DomesticCounterpartyBankValidationRules: EntityValidationRules<CounterpartyBank> = {
  accountNumber: {
    required: true,
    isBankAccountNumber: {
      bankCode: false,
      prefix: true,
    },
  },
  bank: {
    required: "fillBankCode",
    isBankCode: {
      codeType: (entity: CounterpartyBank) => null,
    },
  },
  name: {
    isLength: {
      max: 35,
      optional: true,
    },
    isPaymentOrderText: true,
  },
};

export const SepaCounterpartyBankValidationRules: EntityValidationRules<CounterpartyBank> = {
  accountNumber: {
    required: true,
    isLength: {
      max: 34,
    },
    isIBAN: {},
  },
  bank: {
    isBankCode: {
      codeType: (entity: CounterpartyBank) => BankCodeType.Bic,
    },
    isAlphanumericText: true,
  },
  name: {
    isLength: {
      max: 35,
      optional: true,
    },
  },
};

export const ForeignCounterpartyBankValidationRulesFactory = (entity: CounterpartyBank) => {
  const baseValidationRules: EntityValidationRules<CounterpartyBank> = {
    accountNumber: {
      required: true,
      isLength: {
        max: 34,
      },
      isAlphanumericText: true,
    },
    codeType: {
      requiredIf: {
        condition: (entity: CounterpartyBank) => !!entity.bank,
      },
    },
    bank: {
      isBankCode: {
        codeType: (entity: CounterpartyBank) => entity.codeType,
      },
      isLength: {
        max: 11,
        optional: true,
      },
      isAlphanumericText: true,
    },
    name: {
      requiredIf: {
        condition: (entity: CounterpartyBank) =>
          entity.codeType !== BankCodeType.Bic || (!entity.bank && entity.codeType !== BankCodeType.Bic),
        customMessage: "fillThisOrBankCodeBIC",
      },
      isLength: {
        max: 35,
        optional: true,
      },
    },
    cityAddress: {
      requiredIf: {
        condition: (entity: CounterpartyBank) =>
          entity.codeType !== BankCodeType.Bic || (!entity.bank && entity.codeType !== BankCodeType.Bic),
        customMessage: "fillThisOrBankCodeBIC",
      },
      isLength: {
        max: 35,
        optional: true,
      },
    },
  };

  let validationRules:
    | EntityValidationRules<RefundCounterpartyBank | TransferCounterpartyBankResponse>
    | EntityValidationRules<TransferRefundCounterpartyBankRequest | CustomerRefundCandidateCounterpartyBank>;

  if (entity instanceof RefundCounterpartyBank || entity instanceof TransferCounterpartyBankResponse) {
    validationRules = {
      ...baseValidationRules,
      countryCode: {
        requiredIf: {
          condition: (entity: RefundCounterpartyBank) =>
            entity.codeType !== BankCodeType.Bic || (!entity.bank && entity.codeType !== BankCodeType.Bic),
          customMessage: "fillThisOrBankCodeBIC",
        },
        isLength: {
          max: 2,
          optional: true,
        },
      },
    };
  } else {
    validationRules = {
      ...baseValidationRules,
      countryId: {
        requiredIf: {
          condition: (entity: CustomerRefundCandidateCounterpartyBank | TransferRefundCounterpartyBankRequest) =>
            entity.codeType !== BankCodeType.Bic || (!entity.bank && entity.codeType !== BankCodeType.Bic),
          customMessage: "fillThisOrBankCodeBIC",
        },
      },
    };
  }

  return validationRules;
};
